import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Dialog, PageTitle, pillClass, SelectField, TextField } from '@rooming/ui';
import { useState } from 'react';
import { backoffice, type ExecutiveAuditItem } from '../api';
import { Loaded } from '../common';
import { useSession } from '../session';

// Executive audit (ADR-0013). The server tells every entry in plain Spanish; this screen only lays it out: date, hotel,
// actor (name and email), the action in words, and a tidy key-value detail. No JSON, no codes. Super administrator only.

const VIEWS = [
  ['actions', 'Acciones'],
  ['beds', 'Cambios de cama'],
] as const;
type View = (typeof VIEWS)[number][0];

const when = (iso: string): string =>
  new Intl.DateTimeFormat('es', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));

/** Start of a local day as an instant with offset (the API requires one). */
const dayStart = (day: string, plusDays = 0): string | undefined => {
  if (day === '') return undefined;
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + plusDays);
  return date.toISOString();
};

export function ExecutiveAuditPage() {
  const { hasRole } = useSession();
  const [view, setView] = useState<View>('actions');
  const [hotelId, setHotelId] = useState('');
  const [actorUserId, setActorUserId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cursors, setCursors] = useState<string[]>([]);
  const [open, setOpen] = useState<ExecutiveAuditItem>();
  const allowed = hasRole('TENANT_ADMIN');

  const hotels = useQuery({ queryKey: ['hotels'], queryFn: backoffice.hotels, enabled: allowed });
  const staff = useQuery({
    queryKey: ['audit-executive', 'staff-users'],
    queryFn: backoffice.staffUsers,
    enabled: allowed,
  });
  const external = useQuery({
    queryKey: ['audit-executive', 'external-users'],
    queryFn: backoffice.externalUsers,
    enabled: allowed,
  });
  const types = useQuery({
    queryKey: ['audit-executive', 'types'],
    queryFn: backoffice.executiveAuditActionTypes,
    enabled: allowed,
  });
  const cursor = cursors.at(-1);
  const range = { from: dayStart(from), to: dayStart(to, 1) };
  const events = useQuery({
    queryKey: ['audit-executive', view, hotelId, actorUserId, action, from, to, cursor ?? ''],
    queryFn: () =>
      backoffice.executiveAudit({
        source: view,
        ...(hotelId === '' ? {} : { hotelId }),
        ...(actorUserId === '' ? {} : { actorUserId }),
        ...(action === '' || view === 'beds' ? {} : { action }),
        ...(range.from === undefined ? {} : { from: range.from }),
        ...(range.to === undefined ? {} : { to: range.to }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <Alert tone="warning" title="Acceso denegado">
        Esta sección es solo del súper administrador.
      </Alert>
    );
  }

  const reset = (): void => {
    setCursors([]);
  };
  const people = [
    ...(staff.data?.items ?? []).map((u) => ({ id: u.id, text: `${u.displayName} (${u.email})` })),
    ...(external.data?.items ?? []).map((u) => ({ id: u.id, text: `${u.displayName} (${u.email})` })),
  ];

  return (
    <>
      <PageTitle subtitle="Qué pasó, dónde, quién lo hizo y por qué, contado en palabras. Solo lo ve el súper administrador.">
        Auditoría ejecutiva
      </PageTitle>
      <div className="flex flex-col gap-4">
        <div role="tablist" aria-label="Tipo de registro" className="flex flex-wrap gap-2">
          {VIEWS.map(([key, text]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={view === key}
              className={pillClass(view === key)}
              onClick={() => {
                setView(key);
                reset();
              }}
            >
              {text}
            </button>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <TextField
            label="Desde el día"
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              reset();
            }}
          />
          <TextField
            label="Hasta el día"
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              reset();
            }}
          />
          <SelectField
            label="Hotel"
            options={[
              { value: '', label: 'Todos los hoteles' },
              ...(hotels.data ?? []).map((h) => ({ value: h.id, label: `${h.name} · ${h.city}` })),
            ]}
            value={hotelId}
            onChange={(event) => {
              setHotelId(event.target.value);
              reset();
            }}
          />
          <SelectField
            label="Usuario"
            options={[
              { value: '', label: 'Todos los usuarios' },
              ...people.map((p) => ({ value: p.id, label: p.text })),
            ]}
            value={actorUserId}
            onChange={(event) => {
              setActorUserId(event.target.value);
              reset();
            }}
          />
          {view === 'actions' && (
            <SelectField
              label="Tipo de acción"
              options={[
                { value: '', label: 'Todas las acciones' },
                ...(types.data?.items ?? []).map((t) => ({ value: t.value, label: t.label })),
              ]}
              value={action}
              onChange={(event) => {
                setAction(event.target.value);
                reset();
              }}
            />
          )}
        </div>

        <Loaded query={events}>
          {(page) => (
            <>
              {page.items.length === 0 ? (
                <p className="text-muted">No hay registros para estos filtros.</p>
              ) : (
                <ul className="flex flex-col gap-3" aria-label="Registros de auditoría">
                  {page.items.map((item) => (
                    <li key={`${item.source}-${item.id}`}>
                      <Card>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-col gap-1">
                            <p className="text-sm text-muted">
                              {when(item.occurredAt)} · {item.hotel?.name ?? 'Sin hotel asociado'} · {item.category}
                            </p>
                            <p className="font-medium">{item.summary}</p>
                            <p className="text-sm text-muted">
                              {item.actor === null
                                ? 'El sistema'
                                : `${item.actor.name}${item.actor.email === null ? '' : ` · ${item.actor.email}`}${item.actor.role === null ? '' : ` · ${item.actor.role}`}`}
                            </p>
                          </div>
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setOpen(item);
                            }}
                          >
                            Ver detalle
                          </Button>
                        </div>
                      </Card>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={cursors.length === 0}
                  onClick={() => {
                    setCursors(cursors.slice(0, -1));
                  }}
                >
                  Más recientes
                </Button>
                <Button
                  variant="secondary"
                  disabled={page.nextCursor === null}
                  onClick={() => {
                    if (page.nextCursor !== null) setCursors([...cursors, page.nextCursor]);
                  }}
                >
                  Más antiguos
                </Button>
              </div>
            </>
          )}
        </Loaded>
      </div>

      <Dialog
        open={open !== undefined}
        title="Detalle del registro"
        onClose={() => {
          setOpen(undefined);
        }}
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(undefined);
            }}
          >
            Cerrar
          </Button>
        }
      >
        {open !== undefined && (
          <div className="flex flex-col gap-3">
            <p className="font-medium">{open.summary}</p>
            <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[auto_1fr]">
              <Row label="Fecha y hora" value={when(open.occurredAt)} />
              <Row label="Hotel" value={open.hotel?.name ?? 'Sin hotel asociado'} />
              <Row label="Realizado por" value={open.actor?.name ?? 'El sistema'} />
              {open.actor?.email !== undefined && open.actor.email !== null && (
                <Row label="Correo" value={open.actor.email} />
              )}
              {open.actor?.role !== undefined && open.actor.role !== null && (
                <Row label="Rol" value={open.actor.role} />
              )}
              <Row label="Categoría" value={open.category} />
              {open.details.map((detail, index) => (
                <Row key={`${detail.label}-${String(index)}`} label={detail.label} value={detail.value} />
              ))}
            </dl>
          </div>
        )}
      </Dialog>
    </>
  );
}

function Row({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <>
      <dt className="text-sm text-muted">{label}</dt>
      <dd>{value}</dd>
    </>
  );
}
